import { useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { Card } from 'shared/components';
import { Button } from '@orbita-ui/core';
import { FormWithValidation } from 'shared/form';
import { useForm } from 'react-hook-form';
import { useCoreService } from 'core/services/useCoreService';
import { useDataFetcher } from 'shared/http';
import { Cuenta } from 'core/cuenta/models/Cuenta';
import { CentroCosto } from 'core/centrocosto/models/CentroCosto';
import { CostCentreFormSkeleton } from './CostCentreFormSkeleton';
import { SelectOption } from 'shared/form/models/SelectOption';
import { PBody } from 'shared/components/Fonts';
import { COST_CENTRE_LIST_PATH } from 'core/centrocosto/routes';
import { useNavigate } from 'react-router-dom';
import {
  SAVE_COST_CENTRE_ERROR_MESSAGES,
  SaveCostCentreApiErrors,
} from 'core/centrocosto/constants/SaveCostCentreMessages';

export interface CostCentreFormData {
  centroCosto: SelectOption<CentroCosto>;
  denominacion: string;
  cuenta: SelectOption<Cuenta>;
}
interface CostCentreFormProps {
  onSubmit: (data: CostCentreFormData) => void;
  centroCosto?: CentroCosto;
  submitLabel?: string;
  fields?: {
    centroCosto?: boolean;
    cuenta?: boolean;
    denominacion?: boolean;
  };
  title?: string;
  isLoading?: boolean;
  apiErrors?: SaveCostCentreApiErrors;
}

export function CostCentreForm({
  onSubmit,
  centroCosto,
  submitLabel = 'Guardar',
  fields = { centroCosto: true, cuenta: true, denominacion: true },
  title = 'Datos del Centro de Costos',
  isLoading = false,
  apiErrors,
}: CostCentreFormProps) {
  const methods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      centroCosto: null,
      cuenta: null,
      denominacion: '',
    },
  });
  const { setError } = methods;

  const coreService = useCoreService();
  const [cuentas, fetchCuentas] = useDataFetcher<SelectOption<Cuenta>[]>();
  const [centroCostos, fetchCentroCostos] = useDataFetcher<SelectOption<CentroCosto>[]>();
  const navigate = useNavigate();

  // Manejo de errores
  useEffect(() => {
    if (apiErrors?.cost_centre_name_not_available) {
      setError('denominacion', {
        type: 'custom',
        message: SAVE_COST_CENTRE_ERROR_MESSAGES.cost_centre_name_not_available,
      });
    }
    if (apiErrors?.account_not_available) {
      setError('centroCosto', {
        type: 'custom',
        message: SAVE_COST_CENTRE_ERROR_MESSAGES.account_not_available,
      });
    }
    if (apiErrors?.account_not_visible) {
      setError('cuenta', {
        type: 'custom',
        message: SAVE_COST_CENTRE_ERROR_MESSAGES.account_not_visible,
      });
    }
  }, [apiErrors, setError]);

  useEffect(() => {
    fetchCuentas(async () => {
      const cuentas = await coreService.getUnnasignedAccounts();
      return (
        cuentas?.map((cuenta) => ({
          label: `${cuenta.numeroCuenta} - ${cuenta.denominacion}`,
          value: cuenta,
        })) ?? []
      );
    });
    fetchCentroCostos(async () => {
      const pagedResponse = await coreService.getCostCentres();
      return (
        pagedResponse.content?.map((centroCosto) => ({
          label: centroCosto.nombre,
          value: centroCosto,
        })) ?? []
      );
    });
  }, [coreService, fetchCuentas, fetchCentroCostos]);

  useEffect(() => {
    if (centroCosto) {
      methods.setValue('denominacion', centroCosto.nombre);
      methods.setValue('centroCosto', String(centroCosto.id));
    }
  }, [centroCosto, methods]);

  if (cuentas.isLoading || centroCostos.isLoading || isLoading) {
    return <CostCentreFormSkeleton title={title} />;
  }

  if (!cuentas.data || (cuentas.data.length === 0 && fields.cuenta !== false)) {
    return (
      <Row className="mb-3">
        <Col>
          <Card variant="info">
            <Card.Body $spacing>
              <PBody className="fw-semibold">No hay cuentas disponibles</PBody>
              Actualmente no existen cuentas disponibles para asignar a un nuevo centro de
              costos. Es necesario contar con al menos una cuenta disponible para poder
              crear un centro de costos
              <Row>
                <Col className="text-end">
                  <Button onClick={() => navigate(COST_CENTRE_LIST_PATH)}>
                    Volver al listado de Centros de Costos
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <FormWithValidation onSubmit={onSubmit} reactFormHookMethods={methods}>
      <Card>
        <Card.Title>{title}</Card.Title>
        <Card.Body className="p-3">
          {fields.centroCosto && (
            <div className="mb-2">
              <FormWithValidation.LegacySelect
                name="centroCosto"
                label="Depende de"
                placeholder={
                  centroCosto?.nombreCentroCostoPadre || 'Seleccione un Centro de Costos'
                }
                options={centroCostos.data}
                registerOptions={{ required: 'Campo Obligatorio' }}
              />
            </div>
          )}

          {fields.denominacion && (
            <div className="mb-2">
              <FormWithValidation.TextInput
                name="denominacion"
                label="Denominación"
                fullWidth
                registerOptions={{ required: 'Campo Obligatorio' }}
              />
            </div>
          )}

          {fields.cuenta && (
            <FormWithValidation.LegacySelect
              name="cuenta"
              label="Cuenta"
              placeholder="Seleccione una Cuenta"
              options={cuentas.data}
              registerOptions={{ required: 'Campo Obligatorio' }}
            />
          )}
        </Card.Body>
        <Card.Footer>
          <Row>
            <Col>
              <Button className="float-end" type="submit">
                {submitLabel}
              </Button>
            </Col>
          </Row>
        </Card.Footer>
      </Card>
    </FormWithValidation>
  );
}
